import type { GetImpulseFormOutput, GetImpulseFormOutputVerbose } from "~/tools/impulse-form";

import type { TimeSeriesChart } from "../../../../common/dashboard-spec/chart-schemas";
import type { WidgetFormSync } from "../../../widget-form/sync";
import { TimeSeriesChartFormState } from "../../../widget-form/time-series-chart-form-state";
import type { FormProcessor } from "../../_form-processor";
import { FormState as LegendFormState } from "../_legend/form-state";
import { StepFormState as ThresholdStepFormState } from "../_thresholds/step-form-state";

type Spec = TimeSeriesChart["spec"];

const DEFAULT = TimeSeriesChartFormState.default;

function specToInput({ visual, yAxis, legend, thresholds }: Spec) {
	return {
		unit: yAxis?.format?.unit ?? DEFAULT.yAxis.element.unit,
		decimals: yAxis?.format?.decimalPlaces?.toString() ?? DEFAULT.yAxis.element.decimals,

		min: yAxis?.min?.toString() ?? DEFAULT.range.min,
		max: yAxis?.max?.toString() ?? DEFAULT.range.max,
		connectNulls: visual?.connectNulls ?? DEFAULT.connectNulls,

		legend: LegendFormState.specToInput(legend),

		yAxis: {
			enabled: yAxis?.show ?? DEFAULT.yAxis.enabled,
			label: yAxis?.label ?? DEFAULT.yAxis.element.label,
		},

		thresholds: {
			mode: thresholds?.mode ?? DEFAULT.thresholds.mode,
			steps: thresholds?.steps.map(ThresholdStepFormState.specToInput) ?? DEFAULT.thresholds.steps,
		},
	} satisfies WidgetFormSync;
}

type FormState = TimeSeriesChartFormState;

function outputToSpec({ connectNulls, range, legend, yAxis, thresholds }: GetImpulseFormOutput<FormState>): Spec {
	return {
		visual: { connectNulls },

		legend: LegendFormState.outputToSpec(legend),

		yAxis: {
			show: yAxis != null,
			format: {
				unit: yAxis?.unit,
				decimalPlaces: yAxis?.decimals,
			},
			label: yAxis?.label,
			min: range.min,
			max: range.max,
		},

		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps.map(ThresholdStepFormState.outputToSpec),
		},
	};
}

function verboseOutputToSpec({
	connectNulls,
	range,
	legend,
	yAxis,
	thresholds,
}: GetImpulseFormOutputVerbose<FormState>): Spec {
	return {
		visual: { connectNulls: connectNulls ?? undefined },

		legend: LegendFormState.verboseOutputToSpec(legend),

		yAxis: {
			show: yAxis.enabled ?? undefined,
			format: {
				unit: yAxis.element.unit ?? undefined,
				decimalPlaces: yAxis.element.decimals ?? undefined,
			},
			label: yAxis.element.label ?? undefined,
			min: range.min ?? undefined,
			max: range.max ?? undefined,
		},

		thresholds: {
			mode: thresholds.mode ?? undefined,
			steps: thresholds.steps.map(ThresholdStepFormState.verboseOutputToSpec),
		},
	};
}

type Input = ReturnType<typeof specToInput>;

function init(sync?: Input): FormState {
	const initial = sync == null ? undefined : TimeSeriesChartFormState.fromSync(sync);

	return TimeSeriesChartFormState.init(initial);
}

function setInput(input: Input, state: FormState): void {
	TimeSeriesChartFormState.setInput(TimeSeriesChartFormState.fromSync(input), state);
}

function resetToDefault(state: FormState): void {
	TimeSeriesChartFormState.setInput(TimeSeriesChartFormState.default, state);
}

const FormState = {
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
	init,
	setInput,
	resetToDefault,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
